import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import useForm from 'react-hook-form';
import queryString from 'query-string';
import { useTranslation } from 'react-i18next';
import {
  getContractPdf,
  sendEmails,
  makePayment,
} from '../../redux/modules/payment';
import SuccessContent from './PageContent/SuccessContent';
import ErrorContent from './PageContent/ErrorContent';
import EfesContentHeader from '../EfesContentHeader';
import ScrollToTopPage from '../ScrollToTopPage';
import { errorTexts, PAYMENT_FAILED_ERROR_CODE } from './options';
import { INSURANCE_CONTRACT_TYPES, SUCCESS_SCREEN_PDF_REQUEST_LIMIT } from '../../constants';
import { EMAIL_VALIDATION_PATTERN } from '../../constants/patterns';
import { useClearAllNotifications } from '../../hooks/useClearAllNotifications';

import styles from './PaymentConfirmation.module.scss';

const handleEmailValidation = (email) => EMAIL_VALIDATION_PATTERN.test(String(email).toLowerCase());

const ContractConfirmation = (props) => {
  const {
    headerText,
    headerImage,
    type,
    loadingOrSavingInCache,
    getDraftContractAndSaveInCache,
    hasPdf,
    imageWidth,
  } = props;

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  useClearAllNotifications();

  const { pdf, loadingPdf, sendingEmail, gettingPaymentUrl } = useSelector(
    (state) => state.paymentReducer,
  );

  const [success, setSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState(false);
  const [remainingRequest, setRemainingRequest] = useState(SUCCESS_SCREEN_PDF_REQUEST_LIMIT);

  const { register, handleSubmit, errors, setError, setValue, getValues, clearError } = useForm({
    defaultValues: {
      emails: '',
    },
  });
  const urlParams = useMemo(() => queryString.parse(location.search), [location.search]);

  const cascoContractId = useMemo(() => urlParams.appaCascoId || null, [urlParams]);

  useEffect(() => {
    let timer;
    const isSuccess = urlParams.success === '1';
    const shouldDownloadPdf = (urlParams.ptoken
        && !loadingPdf
        && isSuccess
        && remainingRequest
        && !pdf.length);

    if (shouldDownloadPdf && hasPdf) {
      timer = setTimeout(() => {
        if (type !== INSURANCE_CONTRACT_TYPES.CMTPL) {
          dispatch(getContractPdf(type, urlParams.ptoken));
        } else if (cascoContractId) {
          dispatch(getContractPdf(INSURANCE_CONTRACT_TYPES.CMTPL_CASCO, urlParams.ptoken));
        }
        setRemainingRequest((state) => state - 1);
      }, 5000);
    }

    return () => clearTimeout(timer);
  }, [
    urlParams.ptoken,
    urlParams.success,
    dispatch,
    loadingPdf,
    location.pathname,
    location.search,
    pdf.length,
    remainingRequest,
    type,
    cascoContractId,
    hasPdf,
  ]);

  useEffect(() => {
    register({ name: 'emails' });
  }, [register]);

  useEffect(() => {
    const urlSuccess = urlParams.success;
    const urlError = urlParams.error;
    if (!urlSuccess || urlSuccess === '0') {
      setSuccess(false);
      if (urlError === PAYMENT_FAILED_ERROR_CODE) {
        setPaymentError(true);
      }
    } else if (urlSuccess === '1') {
      setSuccess(true);
      // Dispatch user login action after login story approve
    }
  }, [dispatch, location.search, urlParams]);

  const tryAgain = () => {
    dispatch(getDraftContractAndSaveInCache(urlParams.id, urlParams.token)).then(
      ({ id, draftToken }) => {
        dispatch(makePayment(id, urlParams.type, draftToken));
      },
    );
  };

  const handleSendEmails = useCallback(
    (id, emails) => {
      dispatch(sendEmails(id, emails));
    },
    [dispatch],
  );

  const onSubmit = (data) => {
    clearError(undefined);
    if (!data.emails) {
      setError('emails', 'invalid', 'validationErrors.required');
      return null;
    }
    const arrayOfEmails = data.emails.split(',').map((email) => email.trim());
    const invalidEmail = arrayOfEmails.find((email) => !handleEmailValidation(email));

    if (invalidEmail) {
      setError('emails', 'invalid', invalidEmail);
      return null;
    }

    const emails = {
      email: arrayOfEmails.join(';'),
      type,
    };

    const contractId = queryString.parse(location.search).id;
    if (type !== INSURANCE_CONTRACT_TYPES.CMTPL) {
      handleSendEmails(contractId, emails);
    } else if (cascoContractId) {
      handleSendEmails(cascoContractId, { ...emails, type: INSURANCE_CONTRACT_TYPES.CMTPL_CASCO });
    }
    return null;
  };

  const content = useMemo(
    () => {
      if (!success) {
        const error = errorTexts[urlParams.error]
          ? errorTexts[urlParams.error]
          : errorTexts.default;
        return (
          <ErrorContent
            isLoading={loadingOrSavingInCache || gettingPaymentUrl}
            paymentError={paymentError}
            errorDescription={t(error.description)}
            errorTitle={t(error.title)}
            tryAgain={tryAgain}
            errorCode={urlParams.error}
          />
        );
      }
      return (
        <SuccessContent
          clearError={clearError}
          getValues={getValues}
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          pdf={pdf}
          remainingRequest={remainingRequest}
          hasPdf={hasPdf}
          sendingEmail={sendingEmail}
          setValue={setValue}
          errors={errors}
        />
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      t,
      clearError,
      errors,
      getValues,
      gettingPaymentUrl,
      handleSubmit,

      loadingOrSavingInCache,
      paymentError,
      pdf,
      remainingRequest,
      sendingEmail,
      setValue,
      success,
      type,
      urlParams,
    ],
  );

  return (
    <>
      <ScrollToTopPage />
      <EfesContentHeader
        headerText={headerText}
        headerImage={headerImage}
        imageWidth={imageWidth}
      />
      <div className={styles.container}>
        <div className={styles.content}>{content}</div>
      </div>
    </>
  );
};

ContractConfirmation.propTypes = {
  headerText: PropTypes.string.isRequired,
  headerImage: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  loadingOrSavingInCache: PropTypes.bool.isRequired,
  getDraftContractAndSaveInCache: PropTypes.func.isRequired,
  hasPdf: PropTypes.bool,
  imageWidth: PropTypes.number,
};

ContractConfirmation.defaultProps = {
  hasPdf: true,
  imageWidth: 70,
};

export default withRouter(ContractConfirmation);
